import { DateTime } from "luxon";
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { Roles, useHasRole } from "../../AuthProvider";
import { CustomTextField } from "../../components/Inputs/CustomTextField";
import WeekDaySelector from "../../components/Inputs/WeekDaySelector";
import { DepartureRow } from "../types";

interface Props {
  rows: DepartureRow[];
  onChange: (rows: DepartureRow[]) => void;
}

export default function DepartureManagement({ rows, onChange }: Props) {
  const { hasRole } = useHasRole();
  const showNightlyRate = hasRole(Roles.Pricer);
  const selectedIndices = rows
    .map((r, i) => (r.selected ? i : -1))
    .filter((i) => i >= 0);

  const allSelected = rows.length > 0 && selectedIndices.length === rows.length;

  const toggleSelectAll = () => {
    const newSelected = !allSelected;
    onChange(rows.map((r) => ({ ...r, selected: newSelected })));
  };

  const toggleRow = (index: number) => {
    onChange(
      rows.map((r, i) => (i === index ? { ...r, selected: !r.selected } : r))
    );
  };

  const updateField = (
    index: number,
    field: keyof DepartureRow,
    value: string | string[]
  ) => {
    const isSelected = rows[index]?.selected;

    onChange(
      rows.map((r, i) => {
        if (i === index || (isSelected && r.selected)) {
          return { ...r, [field]: value, persisted: false };
        }
        return r;
      })
    );
  };

  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={allSelected}
                indeterminate={selectedIndices.length > 0 && !allSelected}
                onChange={toggleSelectAll}
                size="small"
                slotProps={{
                  input: { "aria-label": "Sélectionner toutes les lignes" },
                }}
              />
            </TableCell>
            <TableCell sx={{ color: "text.secondary" }}>Date départ</TableCell>
            <TableCell sx={{ color: "text.secondary" }}>Prix Camping</TableCell>
            <TableCell sx={{ color: "text.secondary" }}>Prix Psycho</TableCell>
            <TableCell sx={{ color: "text.secondary" }}>Min Stay</TableCell>
            <TableCell sx={{ color: "text.secondary" }}>
              Jour arrivée possible
            </TableCell>
            {showNightlyRate && (
              <TableCell sx={{ color: "text.secondary" }}>
                Prix Campings.com
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row._key} selected={row.selected}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={row.selected}
                  onChange={() => toggleRow(index)}
                  size="small"
                  slotProps={{
                    input: {
                      "aria-label": `Sélectionner la ligne ${index + 1}`,
                    },
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {row.startDate
                    ? DateTime.fromISO(row.startDate).toFormat("dd/MM/yyyy")
                    : ""}
                </Typography>
              </TableCell>
              <TableCell>
                <CustomTextField
                  type="number"
                  value={row.price}
                  onChange={(e) => updateField(index, "price", e.target.value)}
                  required={false}
                />
              </TableCell>
              <TableCell>
                <CustomTextField
                  type="number"
                  value={row.pricingConstraint}
                  onChange={(e) =>
                    updateField(index, "pricingConstraint", e.target.value)
                  }
                  required={false}
                />
              </TableCell>
              <TableCell>
                <CustomTextField
                  type="number"
                  value={row.minStay}
                  onChange={(e) =>
                    updateField(index, "minStay", e.target.value)
                  }
                  required={false}
                />
              </TableCell>
              <TableCell>
                <WeekDaySelector
                  limitTags={2}
                  multiple
                  value={row.possibleArrivalDays}
                  onChange={(days) =>
                    updateField(index, "possibleArrivalDays", days)
                  }
                  sx={{ minWidth: 250, maxWidth: 250 }}
                />
              </TableCell>
              {showNightlyRate && (
                <TableCell>
                  <CustomTextField
                    type="number"
                    value={row.currentNightlyRate}
                    onChange={(e) =>
                      updateField(index, "currentNightlyRate", e.target.value)
                    }
                    required={false}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
